<template>
  <div v-if="contest.resultMethod === 'multi_seat_instant_runoff'"  class="vstack gap-3">
    <AVInstantRunoffSummary
      v-for="(seat, index) in result.resultDataForDisplay"
      :sortedResult="seat.sortedResult"
      :rounds="result.resultData.seatsData[Number(index)].rounds"
      :seatNumber="Number(index) + 1"
      :quota="result.resultData.seatsData[Number(index)].quota"
      :totalVotes="result.resultData.seatsData[Number(index)].totalVotes"
      :blankVotes="result.resultData.seatsData[Number(index)].exclusiveOptionCounts.blank || 0"
      :votesNotIncluded="seat.votesNotIncluded"
      :vote-counts="result.voterCounts"
    />
  </div>
  <AVInstantRunoffSummary
    v-else-if="contest.resultMethod === 'instant_runoff'"
    :sortedResult="result.resultDataForDisplay"
    :rounds="result.resultData.rounds"
    :seatNumber="1"
    :quota="result.resultData.quota"
    :totalVotes="result.resultData.totalVotes"
    :blankVotes="result.resultData.exclusiveOptionCounts.blank || 0"
    :votesNotIncluded="null"
    :vote-counts="result.voterCounts"
  />
  <AVRankedSummary
    v-else-if="contest.resultMethod === 'prioritization'"
    :result="result.resultDataForDisplay"
    :distribution-number="result.resultData.distributionNo"
    :hide-tied="!votingRoundReport.colorTied"
    :hide-elected="!votingRoundReport.colorElected"
    :seats="contest.seats ?? 1"
    :vote-counts="result.voterCounts"
  />
  <AVDhondtSummary
    v-else-if="isHighestAverageMethod()"
    :result="result.resultDataForDisplay"
    :total-count="totalCount"
    :distribution-number="result.resultData.distributionNo"
    :hide-tied="!votingRoundReport.colorTied"
    :hide-elected="!votingRoundReport.colorElected"
    :seats="contest.seats ?? 1"
    :vote-counts="result.voterCounts"
  />
  <AVDhondtAPSummary
    v-else-if="contest.resultMethod === 'dhondt_ap'"
    :result="result.resultDataForDisplay"
    :total-count="totalCount"
    :distribution-number="result.resultData.distributionNo"
    :hide-tied="!votingRoundReport.colorTied"
    :hide-elected="!votingRoundReport.colorElected"
    :seats="contest.seats ?? 1"
    :vote-counts="result.voterCounts"
  />
  <AVNormalSummary
    v-else
    :sortedResult="result.resultDataForDisplay"
    :total-count="totalCount"
    :hide-percentage="!contest.resultPercentages"
    :hide-tied="!votingRoundReport.colorTied"
    :hide-elected="!votingRoundReport.colorElected"
    :disregard-blank="contest.disregardBlankVotes"
    :vote-counts="result.voterCounts"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  Result,
  PropType,
  E2E,
  ConferenceContest,
  ConferenceVotingRoundReport,
} from "@/types";

const props = defineProps({
  resultOrchestrator: {
    type: Object as PropType<E2E.ResultOrchestrator>,
    required: true,
  },
  votingRoundReport: {
    type: Object as PropType<ConferenceVotingRoundReport>,
    required: true,
  },
  contest: {
    type: Object as PropType<ConferenceContest>,
    required: true,
  },
  result: {
    type: Object as PropType<Result>,
    required: true,
  },
});

const isHighestAverageMethod = () => {
  return ["dhondt", "sainte_lague", "modified_sainte_lague", "imperiali", "danish", "huntington_hill"].includes(props.contest.resultMethod);
};

const totalCount = computed(() => {
  if (props.result === null || props.result.resultData === null) return 0;
  if (props.contest.disregardBlankVotes) {
    return Object.entries(props.result.resultData?.optionCounts)
      .filter(([reference, _count]) => !props.result.resultData?.exclusiveOptions.includes(reference.toString()))
      .reduce((sum: number, [_reference, count]: any[]) => sum + count, 0);
  } else if (props.contest.consensusRequired && props.contest.countUnvoicedVotesAsBlank) {
    return props.contest.disregardWeights
      ? props.result.voterCounts.activeEligiblesOrVoted
      : props.result.voterCounts.activeEligiblesOrVotedWeight;
  } else {
    const excludedOptions = props.result.resultData?.exclusiveOptions.filter(reference => reference !== "blank");
    return Object.entries(props.result.resultData?.optionCounts)
      .filter(([reference, _count]) => !excludedOptions.includes(reference))
      .reduce((sum: number, [_reference, count]: any[]) => sum + count, 0);
  }
});
</script>
